<template>
	<main class="blog-page">
		<section class="blog-hero">
			<div class="blog-container">
				<div class="blog-hero-back">
					<BackLink :to="{ name: 'home' }" label="home" />
				</div>
				<div class="blog-hero-inner">
					<h1 class="blog-hero-title">Notes from the whiteboard</h1>
					<p class="blog-hero-subtitle">
						Practical thoughts on software craft, engineering discipline, and building strong development teams &mdash; in writing and on video, drawn from real-world projects, with the occasional bit of satire.
					</p>
				</div>
			</div>
		</section>

		<section class="blog-featured">
			<div class="blog-container">
				<div class="blog-filter" role="tablist" aria-label="Filter resources">
					<button
						v-for="filterOption in filters"
						:key="filterOption.value"
						type="button"
						role="tab"
						class="blog-filter-pill"
						:class="{ 'is-active': activeFilter === filterOption.value }"
						:aria-selected="activeFilter === filterOption.value"
						@click="activeFilter = filterOption.value"
					>
						{{ filterOption.label }}
					</button>
				</div>

				<div v-if="featuredPost" class="blog-featured-inner">
					<RouterLink :to="{ name: 'resourcePost', params: { slug: featuredPost.slug } }" class="blog-featured-card">
						<div class="blog-featured-grid">
							<div class="blog-featured-media">
								<BlogImage
									:src="cardImage(featuredPost)"
									:fallback-src="cardImageFallback(featuredPost)"
									:alt="featuredPost.imageAlt"
									aspect="16 / 10"
									:priority="true"
									lg-auto
								/>
								<template v-if="isVideo(featuredPost)">
									<span class="blog-media-play" aria-hidden="true">
										<svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
									</span>
									<span v-if="featuredPost.duration" class="blog-media-duration" aria-hidden="true">{{ featuredPost.duration }}</span>
								</template>
							</div>
							<div class="blog-featured-body">
								<div class="blog-featured-tags">
									<CategoryTag :category="featuredPost.category" />
									<span class="blog-featured-latest">Latest</span>
								</div>
								<h2 class="blog-featured-title">{{ featuredPost.title }}</h2>
								<p class="blog-featured-excerpt">{{ featuredPost.excerpt }}</p>
								<PostMeta :date="featuredPost.date" :author="featuredPost.author" class="blog-featured-meta" />
								<span class="blog-read-link blog-featured-read">
									{{ isVideo(featuredPost) ? "Watch video" : "Read post" }}
									<svg class="blog-read-link-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
										<path d="M5 12h14M12 5l7 7-7 7" />
									</svg>
								</span>
							</div>
						</div>
					</RouterLink>
				</div>

				<div v-else class="blog-empty-inner">
					<h2 class="blog-empty-title">{{ emptyTitle }}</h2>
					<p class="blog-empty-text">New {{ activeFilter === "video" ? "videos" : "posts" }} are on the way &mdash; check back soon.</p>
				</div>
			</div>
		</section>

		<section v-if="morePosts.length > 0" class="blog-grid-section">
			<div class="blog-container">
				<div class="blog-grid-inner">
					<h2 class="blog-grid-heading">More {{ gridHeading }}</h2>
					<div class="blog-grid">
						<RouterLink
							v-for="post in morePosts"
							:key="post.slug"
							:to="{ name: 'resourcePost', params: { slug: post.slug } }"
							class="blog-card"
						>
							<div class="blog-card-media">
								<BlogImage :src="cardImage(post)" :fallback-src="cardImageFallback(post)" :alt="post.imageAlt" aspect="16 / 10" />
								<template v-if="isVideo(post)">
									<span class="blog-media-play" aria-hidden="true">
										<svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
									</span>
									<span v-if="post.duration" class="blog-media-duration" aria-hidden="true">{{ post.duration }}</span>
								</template>
							</div>
							<div class="blog-card-body">
								<CategoryTag :category="post.category" />
								<h3 class="blog-card-title">{{ post.title }}</h3>
								<p class="blog-card-excerpt">{{ post.excerpt }}</p>
								<div class="blog-card-footer">
									<PostMeta :date="post.date" :author="post.author" />
									<span class="blog-read-link blog-card-read">
										{{ isVideo(post) ? "Watch" : "Read more" }}
										<svg class="blog-read-link-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
											<path d="M5 12h14M12 5l7 7-7 7" />
										</svg>
									</span>
								</div>
							</div>
						</RouterLink>
					</div>
				</div>
			</div>
		</section>
	</main>
</template>

<script setup>
import { computed, ref } from "vue";
import { resourceItems as posts } from "@/components/blog/resourceItems";
import BackLink from "@/components/blog/BackLink.vue";
import BlogImage from "@/components/blog/BlogImage.vue";
import CategoryTag from "@/components/blog/CategoryTag.vue";
import PostMeta from "@/components/blog/PostMeta.vue";

const filters = [
	{ value: "all", label: "All" },
	{ value: "article", label: "Articles" },
	{ value: "video", label: "Videos" },
];

const activeFilter = ref("all");

function isVideo(post) {
	return post.type === "video";
}

function cardImage(post) {
	if (post.image) {
		return post.image;
	}
	return `https://i.ytimg.com/vi/${post.youTubeId}/maxresdefault.jpg`;
}

function cardImageFallback(post) {
	return isVideo(post) ? `https://i.ytimg.com/vi/${post.youTubeId}/hqdefault.jpg` : "";
}

const filteredPosts = computed(() => {
	if (activeFilter.value === "video") {
		return posts.filter(isVideo);
	}
	if (activeFilter.value === "article") {
		return posts.filter((post) => !isVideo(post));
	}
	return posts;
});

const featuredPost = computed(() => filteredPosts.value[0] ?? null);
const morePosts = computed(() => filteredPosts.value.slice(1));

const gridHeading = computed(() => {
	if (activeFilter.value === "video") {
		return "videos";
	}
	if (activeFilter.value === "article") {
		return "articles";
	}
	return "posts";
});

const emptyTitle = computed(() => (activeFilter.value === "video" ? "No videos yet" : "Nothing here yet"));
</script>

<style scoped>
.blog-page {
	font-family: "Poppins", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
	color: #334155;
}

.blog-container {
	width: 100%;
	max-width: 80rem;
	margin: 0 auto;
	padding: 0 1rem;
}

.blog-hero {
	background-color: #f8fafc;
	padding: 1.5rem 0 4rem;
}

.blog-hero-back {
	margin-bottom: 2.5rem;
}

.blog-hero-inner {
	max-width: 48rem;
	margin: 0 auto;
	text-align: center;
}

.blog-hero-title {
	margin: 0;
	font-size: 2.25rem;
	font-weight: 800;
	line-height: 1.15;
	letter-spacing: -0.025em;
	color: #0f172a;
}

.blog-hero-subtitle {
	margin: 1rem 0 0;
	font-size: 1.125rem;
	line-height: 1.6;
	color: #475569;
}

.blog-featured {
	background-color: #ffffff;
	padding: 5rem 0;
}

.blog-featured-inner {
	max-width: 72rem;
	margin: 0 auto;
}

.blog-featured-card {
	display: block;
	overflow: hidden;
	border-radius: 1rem;
	border: 1px solid #e2e8f0;
	background-color: #ffffff;
	text-decoration: none;
	color: inherit;
	transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.blog-featured-card:hover {
	border-color: #93c5fd;
	box-shadow: 0 10px 15px -3px rgba(15, 23, 42, 0.1), 0 4px 6px -4px rgba(15, 23, 42, 0.1);
}

.blog-featured-grid {
	display: grid;
}

.blog-featured-body {
	display: flex;
	flex-direction: column;
	justify-content: center;
	padding: 1.5rem;
}

.blog-featured-tags {
	display: flex;
	align-items: center;
	gap: 0.75rem;
}

.blog-featured-latest {
	font-size: 0.75rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #1d4ed8;
}

.blog-featured-title {
	margin: 1rem 0 0;
	font-size: 1.5rem;
	font-weight: 800;
	line-height: 1.2;
	letter-spacing: -0.025em;
	color: #0f172a;
}

.blog-featured-excerpt {
	margin: 0.75rem 0 0;
	font-size: 1rem;
	line-height: 1.625;
	color: #475569;
}

.blog-featured-meta {
	margin-top: 1.25rem;
}

.blog-grid-section {
	background-color: #ffffff;
	padding-bottom: 5rem;
}

.blog-grid-inner {
	max-width: 72rem;
	margin: 0 auto;
}

.blog-grid-heading {
	margin: 0;
	font-size: 1.5rem;
	font-weight: 800;
	letter-spacing: -0.025em;
	color: #0f172a;
}

.blog-grid {
	margin-top: 2rem;
	display: grid;
	gap: 1.5rem;
	grid-template-columns: 1fr;
}

.blog-card {
	display: flex;
	flex-direction: column;
	overflow: hidden;
	border-radius: 1rem;
	border: 1px solid #e2e8f0;
	background-color: #ffffff;
	text-decoration: none;
	color: inherit;
	transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.blog-card:hover {
	border-color: #93c5fd;
	box-shadow: 0 4px 6px -1px rgba(15, 23, 42, 0.1), 0 2px 4px -2px rgba(15, 23, 42, 0.1);
}

.blog-card-body {
	display: flex;
	flex: 1;
	flex-direction: column;
	padding: 1.25rem;
}

.blog-card-title {
	margin: 0.75rem 0 0;
	font-size: 1.125rem;
	font-weight: 700;
	line-height: 1.375;
	color: #0f172a;
}

.blog-card-excerpt {
	margin: 0.5rem 0 0;
	font-size: 0.875rem;
	line-height: 1.625;
	color: #475569;
}

.blog-card-footer {
	margin-top: auto;
	padding-top: 1rem;
}

.blog-read-link {
	display: inline-flex;
	width: fit-content;
	align-items: center;
	gap: 0.375rem;
	font-size: 0.875rem;
	font-weight: 600;
	color: #1d4ed8;
	transition: color 0.18s ease;
}

.blog-featured-read {
	margin-top: 1.5rem;
}

.blog-card-read {
	margin-top: 0.75rem;
}

.blog-read-link-icon {
	width: 1rem;
	height: 1rem;
	-webkit-transition: -webkit-transform 0.18s ease;
	transition: transform 0.18s ease;
}

.blog-featured-card:hover .blog-read-link,
.blog-card:hover .blog-read-link {
	color: #1e40af;
}

.blog-featured-card:hover .blog-read-link-icon,
.blog-card:hover .blog-read-link-icon {
	-webkit-transform: translateX(2px);
	transform: translateX(2px);
}

.blog-featured-card:hover :deep(.blog-image-img),
.blog-card:hover :deep(.blog-image-img) {
	-webkit-transform: scale(1.05);
	transform: scale(1.05);
}

.blog-filter {
	display: flex;
	justify-content: center;
	flex-wrap: wrap;
	gap: 0.5rem;
	margin-bottom: 2.5rem;
}

.blog-filter-pill {
	display: inline-flex;
	align-items: center;
	padding: 0.5rem 1.15rem;
	border-radius: 999px;
	border: 1px solid #e2e8f0;
	background-color: #ffffff;
	color: #475569;
	font-family: inherit;
	font-size: 0.9rem;
	font-weight: 600;
	cursor: pointer;
	transition: color 0.18s ease, background 0.18s ease, border-color 0.18s ease;
}

.blog-filter-pill:hover {
	color: #1d4ed8;
	border-color: #93c5fd;
}

.blog-filter-pill.is-active {
	background-color: #2563eb;
	border-color: #2563eb;
	color: #ffffff;
}

.blog-featured-media {
	position: relative;
	min-width: 0;
}

.blog-card-media {
	position: relative;
}

.blog-media-play {
	position: absolute;
	top: 50%;
	left: 50%;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 3.25rem;
	height: 3.25rem;
	border-radius: 999px;
	background: rgba(255, 255, 255, 0.92);
	color: #2563eb;
	box-shadow: 0 10px 22px -10px rgba(15, 39, 66, 0.55);
	-webkit-transform: translate(-50%, -50%);
	transform: translate(-50%, -50%);
	transition: transform 0.2s ease, background 0.2s ease, color 0.2s ease;
	z-index: 1;
}

.blog-media-play svg {
	width: 1.4rem;
	height: 1.4rem;
	margin-left: 0.12rem;
}

.blog-media-duration {
	position: absolute;
	right: 0.6rem;
	bottom: 0.6rem;
	padding: 0.15rem 0.45rem;
	border-radius: 0.35rem;
	background: rgba(15, 23, 42, 0.85);
	color: #ffffff;
	font-size: 0.72rem;
	font-weight: 600;
	z-index: 1;
}

.blog-featured-card:hover .blog-media-play,
.blog-card:hover .blog-media-play {
	-webkit-transform: translate(-50%, -50%) scale(1.08);
	transform: translate(-50%, -50%) scale(1.08);
	background: #2563eb;
	color: #ffffff;
}

.blog-empty-inner {
	max-width: 36rem;
	margin: 0 auto;
	text-align: center;
}

.blog-empty-title {
	margin: 0;
	font-size: 1.5rem;
	font-weight: 800;
	letter-spacing: -0.025em;
	color: #0f172a;
}

.blog-empty-text {
	margin: 0.75rem 0 0;
	font-size: 1rem;
	color: #475569;
}

@media (min-width: 640px) {
	.blog-container {
		padding: 0 1.5rem;
	}

	.blog-hero {
		padding: 2rem 0 5rem;
	}

	.blog-hero-title {
		font-size: 3rem;
	}

	.blog-hero-subtitle {
		font-size: 1.25rem;
	}

	.blog-featured {
		padding: 6rem 0;
	}

	.blog-featured-body {
		padding: 2rem;
	}

	.blog-featured-title {
		font-size: 1.875rem;
	}

	.blog-featured-excerpt {
		font-size: 1.125rem;
	}

	.blog-grid-section {
		padding-bottom: 6rem;
	}

	.blog-grid-heading {
		font-size: 1.875rem;
	}

	.blog-grid {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	.blog-card-body {
		padding: 1.5rem;
	}

	.blog-card-title {
		font-size: 1.25rem;
	}

	.blog-filter {
		margin-bottom: 3rem;
	}
}

@media (min-width: 1024px) {
	.blog-container {
		padding: 0 2rem;
	}

	.blog-hero {
		padding: 2rem 0 6rem;
	}

	.blog-hero-title {
		font-size: 3.75rem;
	}

	.blog-featured-grid {
		grid-template-columns: 1.2fr 1fr;
	}

	.blog-featured-media {
		height: 100%;
	}

	.blog-featured-body {
		padding: 2.5rem;
	}

	.blog-featured-title {
		font-size: 2.25rem;
	}

	.blog-grid {
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 2rem;
	}
}
</style>